import { prefersReducedMotion } from '@theme/utilities';

/**
 * Dibuja el "hilo" de la historia a medida que se hace scroll y revela los pasos.
 *
 * Mejora progresiva: sin JS (o con `prefers-reduced-motion`) el hilo queda
 * completamente dibujado y todos los pasos visibles (lo controla el CSS por
 * defecto con `--draw: 1`). Este script solo AÑADE el efecto cuando procede.
 */
class HistoriaHilo extends HTMLElement {
  #onScroll = null;
  #scrollObserver = null;
  #stepObserver = null;
  #ticking = false;

  connectedCallback() {
    // Respetar la preferencia de menos movimiento: no animar nada.
    if (prefersReducedMotion()) return;
    if (!this.querySelector('.hilo')) return;

    // A partir de aquí el CSS oculta pasos y "desdibuja" el hilo para animarlos.
    this.classList.add('is-ready');
    this.style.setProperty('--draw', '0');

    // Revelar cada paso cuando entra en el viewport.
    this.#stepObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.#stepObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    for (const step of this.querySelectorAll('.hilo__paso')) {
      this.#stepObserver.observe(step);
    }

    // Escuchar el scroll solo mientras la sección está cerca del viewport.
    this.#onScroll = () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#updateDraw();
        this.#ticking = false;
      });
    };

    this.#scrollObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.#onScroll, { passive: true });
          this.#updateDraw();
        } else if (this.#onScroll) {
          window.removeEventListener('scroll', this.#onScroll);
        }
      },
      { threshold: 0 }
    );
    this.#scrollObserver.observe(this);

    this.#updateDraw();
  }

  disconnectedCallback() {
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    this.#scrollObserver?.disconnect();
    this.#stepObserver?.disconnect();
  }

  /**
   * Mapea la posición de la sección dentro del viewport a un progreso 0..1
   * y lo publica como la custom property `--draw`.
   */
  #updateDraw() {
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const start = vh * 0.85; // el hilo empieza a dibujarse cuando la sección entra por abajo
    const end = vh * 0.15; // y termina cuando su base se acerca a la parte alta
    const total = rect.height + (start - end);
    if (total <= 0) {
      this.style.setProperty('--draw', '1');
      return;
    }
    let progress = (start - rect.top) / total;
    progress = Math.max(0, Math.min(1, progress));
    this.style.setProperty('--draw', String(progress));
  }
}

if (!customElements.get('historia-hilo')) {
  customElements.define('historia-hilo', HistoriaHilo);
}
